/*
 * collection-pagination.js
 * JS-owned FLAT pagination for the collection product grid.
 *
 * Mirrors search-pagination.js's design exactly, adapted for collections:
 *   - Native block is collection.products paginated by 250 (collection's real
 *     per-page cap; search is capped at 50 by Shopify, collections are not).
 *   - Flat page size is the merchant-configured `limit` (collection-products.liquid),
 *     not search's fixed 24.
 *   - URL params are cpage/cbase/cend/cflat (collection-specific, so they can't
 *     collide with search's ppage/pbase/pend/pflat if a page somehow runs both).
 *   - Grid has an explicit id (#collection-products-grid) since, unlike search's
 *     #content-products, the collection grid template didn't have one.
 *
 * CONCATENATED-STREAM MODEL
 * -------------------------
 * We treat every block's product cards (already in visible-only, sold-out-last
 * order from collection-products.liquid) as one continuous stream:
 *   stream = block1cards ++ block2cards ++ block3cards ++ ...
 * Flat page N shows stream[(N-1)*perPage : N*perPage]. Pages are sliced from the
 * concatenated stream (NOT per-block), so block boundaries are invisible and
 * there are no short "runt" pages at block edges — only the true last page is
 * short. Total pages = ceil(totalVisibleProducts / perPage).
 *
 * LOCK-UNTIL-MAPPED
 * -----------------
 * Site-version blocking means a block's visible card count can't be predicted
 * from unfetched blocks, so the flat page count can't be estimated. The pager
 * and the "N Items" count stay in a loading state, and the current page starts
 * at 1, until every block is fetched and the exact count is known (`mapped`).
 * That happens once per page load; afterwards paging is instant and never
 * re-locks. A requested ?cflat= is validated (and clamped to page 1 if
 * out-of-range) only once mapping completes.
 *
 * Progressive enhancement: Liquid renders the current window + a fallback pager
 * for no-JS/crawlers; this script takes over once it has (or is fetching) the
 * stream.
 */
(function () {
	if (window.__collectionPaginationInit) return;
	window.__collectionPaginationInit = true;

	var GRID_ID = "collection-products-grid";
	var PAGINATION_SELECTOR = ".collection-pagination";
	var COUNT_SELECTOR = ".ct__total .tl-number";
	var LINK_SELECTOR = ".cf__link--collection-page";
	var CARD_SELECTOR = ".product-item[data-sub]";
	var WINDOW = 2;
	var JUMP = 5;
	var CONCURRENCY = 6;
	var meta = null;
	var perPage = 16;
	var blockSize = 250;
	var totalBlocks = 1;
	var blockCards = {};
	var current = 1;
	var totalFlat = 1;
	var mapped = false;
	var requestedFlat = null;

	function qs(sel, root) { return (root || document).querySelector(sel); }

	function reinit() {
		var gridEl = qs("#" + GRID_ID);
		try {
			if (window.BtCurrency && typeof BtCurrency.convertSilence === "function") {
				BtCurrency.convertSilence(
					window.shopCurrency, BtCurrency.currentCurrency,
					window.jQuery ? jQuery(gridEl).find("span.money") : null
				);
			}
		} catch (e) {}
		try { if (window.BT && BT.applyCustomColorSwatches) BT.applyCustomColorSwatches("#" + GRID_ID); } catch (e) {}
		try { if (window.BT && BT.reLoadReview) BT.reLoadReview("#" + GRID_ID); } catch (e) {}
		try { if (window.BT && BT.initDealCountdown) BT.initDealCountdown("#" + GRID_ID); } catch (e) {}
		try { if (window.BT && BT.popularAddedWishlistItems) BT.popularAddedWishlistItems("#" + GRID_ID); } catch (e) {}
		document.dispatchEvent(new CustomEvent("more-products-loaded"));
	}

	function showLoading() { try { if (window.BT && BT.showLoadingFull) BT.showLoadingFull(); } catch (e) {} }
	function hideLoading() { try { if (window.BT && BT.hideLoadingFull) BT.hideLoadingFull(); } catch (e) {} }

	function scrollToGrid() {
		var el = qs("#view-products") || qs("#" + GRID_ID);
		if (!el) return;
		var top = el.getBoundingClientRect().top + window.pageYOffset - 100;
		window.scrollTo({ top: top < 0 ? 0 : top, behavior: "smooth" });
	}

	// Counts only the contiguous run of blocks starting at 1 — the same run
	// buildStream() concatenates. Summing every cached block instead would count
	// products that sit past a gap and therefore never appear in the stream,
	// producing a total that promises pages the pager can't actually render.
	function exactTotalProducts() {
		var sum = 0;
		for (var b = 1; b <= totalBlocks; b++) {
			if (!blockCards[b]) break;
			sum += blockCards[b].length;
		}
		return sum;
	}

	function recomputeTotal() {
		if (!mapped) return;
		totalFlat = Math.max(1, Math.ceil(exactTotalProducts() / perPage));
		if (current > totalFlat) current = totalFlat;
	}

	function blockURL(b) {
		var u = new URL(window.location.href);
		u.searchParams.set("page", String(b));
		u.searchParams.delete("cpage");
		u.searchParams.delete("cbase");
		u.searchParams.delete("cend");
		return u.toString();
	}

	function cardsFrom(container) {
		var nodes = container.querySelectorAll(CARD_SELECTOR);
		var out = [];
		for (var i = 0; i < nodes.length; i++) {
			var el = nodes[i].cloneNode(true);
			el.classList.remove("d-none");
			out.push(el.outerHTML);
		}
		return out;
	}

	var BLOCK_RETRIES = 2;

	function fetchBlockOnce(b) {
		return fetch(blockURL(b), { headers: { "X-Requested-With": "XMLHttpRequest" } })
			.then(function (res) { if (!res.ok) throw new Error("HTTP " + res.status); return res.text(); })
			.then(function (html) {
				var doc = new DOMParser().parseFromString(html, "text/html");
				var grid = doc.getElementById(GRID_ID);
				// Fetched via raw fetch(), so this markup never went through the
				// DOMContentLoaded ADA sanitization pass that the initially-rendered
				// page got. Sanitize this block's HTML before caching/extracting cards
				// so every page shown by this pager (not just the first) is sanitized.
				if (grid && window.ADASanitizeDescriptionHtml) {
					grid.innerHTML = window.ADASanitizeDescriptionHtml(grid.innerHTML);
				}
				var cards = grid ? cardsFrom(grid) : [];
				blockCards[b] = cards;
				return cards;
			});
	}

	// A block that fails every retry means the exact count/stream can never be
	// trusted for this page load (buildStream's contiguous-from-1 model can't
	// tolerate a permanent gap) — ensureBlock rejects in that case so the caller
	// can fall back to a fresh reload rather than silently show a truncated or
	// undercounted result.
	function ensureBlock(b, attemptsLeft) {
		if (blockCards[b]) return Promise.resolve(blockCards[b]);
		if (attemptsLeft === undefined) attemptsLeft = BLOCK_RETRIES;
		return fetchBlockOnce(b).catch(function (err) {
			if (attemptsLeft <= 0) throw err;
			return ensureBlock(b, attemptsLeft - 1);
		});
	}

	function seedCurrentBlock() {
		var grid = qs("#" + GRID_ID);
		if (!grid) return;
		var cb = meta.currentBlock;
		blockCards[cb] = cardsFrom(grid);
	}

	function buildStream() {
		var stream = [];
		for (var b = 1; b <= totalBlocks; b++) {
			if (blockCards[b]) stream = stream.concat(blockCards[b]);
			else break;
		}
		return stream;
	}

	function showPage(flat, push) {
		if (!mapped) return; // guarded again at call sites, but stay safe if called directly
		if (flat < 1) flat = 1;
		if (flat > totalFlat) flat = 1; // out-of-range manual/stale request -> page 1, not last page
		showLoading();

		var stream = buildStream();
		var start = (flat - 1) * perPage;
		var slice = stream.slice(start, start + perPage);
		var grid = qs("#" + GRID_ID);
		if (grid) grid.innerHTML = "<!--PRODUCT-START-->" + slice.join("") + "<!--PRODUCT-END-->";

		current = flat;
		renderPager();
		reinit();
		if (push !== false) updateURL(flat);
		hideLoading();
		scrollToGrid();
	}

	function fetchBlocks(list) {
		var pending = list.filter(function (b) { return !blockCards[b]; });
		if (!pending.length) return Promise.resolve();
		var idx = 0;
		function worker() {
			if (idx >= pending.length) return Promise.resolve();
			var b = pending[idx++];
			// Do NOT swallow a failure here — after ensureBlock's own retries are
			// exhausted, propagate it so the caller can fall back to a reload
			// instead of silently continuing with a permanent gap in blockCards.
			return ensureBlock(b).then(worker);
		}
		var workers = [];
		for (var i = 0; i < Math.min(CONCURRENCY, pending.length); i++) workers.push(worker());
		return Promise.all(workers);
	}

	function updateURL(flat) {
		try {
			var loc = flatToBlockSub(flat);
			var u = new URL(window.location.href);
			if (flat <= 1) {
				// Page-1 defaults: omit the pager params entirely rather than write
				// out cpage=1&cbase=0&cflat=1 for a page that needs none.
				u.searchParams.delete("page");
				u.searchParams.delete("cpage");
				u.searchParams.delete("cbase");
				u.searchParams.delete("cflat");
			} else {
				u.searchParams.set("page", String(loc.block));
				u.searchParams.set("cpage", String(loc.sub));
				u.searchParams.set("cbase", String(loc.cbase));
				u.searchParams.set("cflat", String(flat));
			}
			u.searchParams.delete("cend");
			window.history.pushState({ path: u.toString(), flat: flat }, "", u.toString());
		} catch (e) {}
	}

	function flatToBlockSub(flat) {
		var target = (flat - 1) * perPage;
		var acc = 0;
		for (var b = 1; b <= totalBlocks; b++) {
			var count = blockCards[b] ? blockCards[b].length : 0;
			if (target < acc + count || b === totalBlocks) {
				var within = target - acc;
				var sub = Math.floor(within / perPage) + 1;
				return { block: b, sub: sub, cbase: Math.floor(acc / perPage) };
			}
			acc += count;
		}
		return { block: 1, sub: 1, cbase: 0 };
	}

	function pageTokens() {
		var tokens = [];
		var last = totalFlat;
		var from = Math.max(1, current - WINDOW);
		var to = Math.min(last, current + WINDOW);

		if (from > 1) {
			tokens.push({ page: 1 });
			if (from > 2) tokens.push({ ellipsis: true, page: Math.max(2, current - JUMP) });
		}
		for (var p = from; p <= to; p++) tokens.push({ page: p });
		if (to < last) {
			if (to < last - 1) tokens.push({ ellipsis: true, page: Math.min(last - 1, current + JUMP) });
			tokens.push({ page: last });
		}
		return tokens;
	}

	function btn(cls, label, page, aria, isCurrent) {
		if (isCurrent) {
			return '<span class="page button button--style1 button--circle button--current dib">' + label + "</span>";
		}
		return '<span class="page"><a href="#" data-page="' + page +
			'" class="button button--style2 button--circle ' + cls + ' dib"' +
			(aria ? ' aria-label="' + aria + '"' : "") + ">" + label + "</a></span>";
	}

	function renderLockedPager() {
		var host = qs(PAGINATION_SELECTOR);
		if (!host) return;
		// Spinner only, no visible text. aria-hidden rather than an
		// aria-live/aria-busy announcement: this is a brief, self-resolving
		// transient state, not something worth interrupting a screen reader
		// user for — the real pager (plain, normally-focusable links) takes
		// over once ready with no special announcement needed.
		host.innerHTML = '<span class="page pagination-loading" aria-hidden="true"><i class="loading-ajax"></i></span>';
		host.setAttribute("data-total-pages", "");
	}

	function renderPager() {
		var host = qs(PAGINATION_SELECTOR);
		if (!host) return;
		if (!mapped) { renderLockedPager(); return; }
		if (totalFlat <= 1) { host.innerHTML = ""; return; }

		var html = "";
		if (current > 1) html += btn(LINK_SELECTOR.slice(1), '<i class="lnr-chevron-left button__prev-page"></i>', current - 1, "Previous", false);

		var toks = pageTokens();
		for (var i = 0; i < toks.length; i++) {
			var t = toks[i];
			if (t.ellipsis) {
				html += btn(LINK_SELECTOR.slice(1) + " button--dot", "...", t.page, "Jump to page " + t.page, false);
			} else {
				html += btn(LINK_SELECTOR.slice(1), String(t.page), t.page, null, t.page === current);
			}
		}

		if (current < totalFlat) html += btn(LINK_SELECTOR.slice(1), '<i class="lnr-chevron-right button__next-page"></i>', current + 1, "Next", false);

		host.innerHTML = html;
		host.setAttribute("data-total-pages", String(totalFlat));
	}

	function renderCount() {
		var host = qs(COUNT_SELECTOR);
		if (!host) return;
		var wrapper = host.closest(".ct__total") || host;
		if (!mapped) {
			// Hidden rather than showing placeholder text while the real count is
			// unknown — no "…" text, just absent until known.
			wrapper.classList.add("d-none");
			return;
		}
		// global-script.js formats .tl-number with thousands separators once at
		// page load; that pass is long done by the time this fires, so match its
		// formatting here rather than leave this element as the one un-comma'd
		// number on the page.
		host.textContent = exactTotalProducts().toLocaleString();
		wrapper.classList.remove("d-none");
	}

	function onSweepFailed() {
		// A permanent gap breaks buildStream's contiguous model, so the count is
		// untrustworthy for this page load — reload for a clean attempt. The
		// sessionStorage guard stops a systemically failing endpoint from
		// looping reloads; after one retry it gives up and stays in the loading
		// state.
		var reloadKey = "collection_pagination_reload_" + (meta && meta.currentBlock ? meta.currentBlock : "");
		try {
			if (sessionStorage.getItem(reloadKey)) return;
			sessionStorage.setItem(reloadKey, "1");
		} catch (e) { return; }
		window.location.reload();
	}

	function onSweepComplete() {
		mapped = true;
		recomputeTotal();
		renderCount();

		// Now that the real total is known, honor whatever page the URL asked
		// for (if any) — validated for the first time here. Out-of-range -> page
		// 1, not the last page, and the URL is cleaned to match.
		var target = requestedFlat && requestedFlat > 0 ? requestedFlat : current;
		if (target > totalFlat) target = 1;
		showPage(target, target !== 1 || requestedFlat > 1);
	}

	function init() {
		var dataEl = document.getElementById("collection-pagination-data");
		if (!dataEl) return;
		try { meta = JSON.parse(dataEl.textContent); } catch (e) { return; }

		perPage = meta.perPage || 16;
		blockSize = meta.blockSize || 250;
		totalBlocks = meta.totalBlocks || 1;

		var u0 = new URL(window.location.href);
		var cflat = parseInt(u0.searchParams.get("cflat") || "", 10);
		requestedFlat = cflat && cflat > 0 ? cflat : null;

		// During the lock (before mapping completes) the grid is simply left as
		// the server rendered it — real, sanitized, correctly-filtered content for
		// whatever native block the URL asked for, just not necessarily flat page
		// 1. See search-pagination.js's matching comment for why we don't try to
		// reslice this to "page 1" ourselves before the real data is known.
		current = 1;

		// Show the locked/loading state immediately — doesn't need to wait on
		// anything, so start it as early as possible.
		renderPager();
		renderCount();

		document.addEventListener("click", function (e) {
			if (!mapped) return; // locked: ignore page-link clicks until the real count is known
			var link = e.target.closest ? e.target.closest(LINK_SELECTOR) : null;
			if (!link) return;
			var page = parseInt(link.getAttribute("data-page"), 10);
			if (!page || page < 1) return;
			e.preventDefault();
			showPage(page, true);
		});

		window.addEventListener("popstate", function (e) {
			if (!mapped) return;
			var u = new URL(window.location.href);
			var flat = parseInt(u.searchParams.get("cflat") || "", 10);
			if (!flat || flat < 1) {
				var cb = parseInt(u.searchParams.get("cbase") || "0", 10) || 0;
				var sub = parseInt(u.searchParams.get("cpage") || "1", 10) || 1;
				flat = cb + sub;
			}
			showPage(flat, false);
		});

		// Every block except the current one is a plain network fetch with no
		// dependency on this page's DOM, so start them immediately rather than
		// waiting on sanitization below.
		var others = [];
		for (var b = 1; b <= totalBlocks; b++) {
			if (b !== meta.currentBlock) others.push(b);
		}
		var othersDone = fetchBlocks(others);

		// seedCurrentBlock() reads the current block out of the live DOM, so it
		// must run after utils.js's ADA sanitization has rebuilt that DOM —
		// otherwise the seeded cards are the only unsanitized ones in the cache.
		// Keyed on the afterSanitization event rather than DOMContentLoaded
		// ordering, which would run this first (utils.js loads later in <body>).
		var seeded = false;
		function seedOnce() {
			if (seeded) return;
			seeded = true;
			seedCurrentBlock();
			// Promise.all, not a chain: a rejection from either side must reach
			// onSweepFailed, and success requires both.
			Promise.all([othersDone, fetchBlocks([meta.currentBlock])])
				.then(onSweepComplete)
				.catch(onSweepFailed);
		}
		if (window.__adaSanitizationDone) {
			seedOnce();
		} else {
			document.addEventListener("afterSanitization", seedOnce, { once: true });
			// Safety net only — covers utils.js failing to load/fire at all.
			setTimeout(seedOnce, 1500);
		}
	}

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", init);
	} else {
		init();
	}
})();
